import AlertMessage from '@components/alerts/AlertMessage';
import AuthSwitchLink from '@components/authSwitchLink/AuthSwitchLink';
import Button from '@components/button/Button';
import { ContactUs } from '@components/contactUsLink/ContactUs';
import InputWithFormItem from '@components/inputWithFormItem/InputWithFormItem';
import { CenterPanel } from '@components/layout/CenterPanel/CenterPanel';
import { SurfaceContainer } from '@components/layout/surfaceContainer/SurfaceContainer';
import { BrandLogo } from '@components/logos/brandLogo/BrandLogo';
import SocialButton from '@components/socialButton/SocialButton';
import { Col, Divider, Form, Row, Space, Typography } from 'antd';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { MdOutlineKey, MdOutlineMailOutline } from 'react-icons/md';
import { connect, useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import {
  loginGoogleUser as loginGoogleUserAction,
  loginMicrosoftUser as loginMicrosoftUserAction,
  loginUser as loginUserAction,
  uiError,
} from '../../actions';
import { SIGN_UP_PATH } from '../signup/helper';

const { Text, Title } = Typography;

interface ISignInFormProps {
  isLoading?: boolean;
  isEmailPasswordError?: boolean;
  errorMessage: string | null;
  loginGoogleUser: () => void;
  loginMicrosoftUser: () => void;
  loginUser: () => void;
}

const SignInForm: React.FC<ISignInFormProps> = ({
  isLoading = false,
  isEmailPasswordError = false,
  errorMessage = null,
  loginGoogleUser,
  loginMicrosoftUser,
  loginUser,
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'loginAndSignup' });

  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
  const location = useLocation();

  const loginWithMagicLink = () => {
    dispatch(uiError({ isError: false, message: null }));
    navigate('/sign-in-link');
  };

  const onChangePassword = () => {
    if (isEmailPasswordError && errorMessage) {
      dispatch(uiError({ isError: false, message: null }));
    }
  };

  return (
    <>
      <Helmet>
        <title>CHAOS dashboard</title>
      </Helmet>
      <CenterPanel>
        <div>
          {errorMessage && !isEmailPasswordError && (
            <AlertMessage className="tw-my-2" showIcon message={errorMessage} />
          )}

          <SurfaceContainer maxWidth={530} noBorder>
            <Space orientation="vertical" size="small" className="tw-p-[16px]">
              <BrandLogo
                size={56}
                wrapperClassName="tw-flex tw-justify-center"
              />

              <Title level={2} className="!tw-m-0 tw-text-center">
                {t('welcomeToChaos')}
              </Title>

              <Text className="tw-text-[--color-text-weaker] tw-flex tw-text-center">
                {t('loginPageIntroText')}
              </Text>

              <Divider className="!tw-my-0 !tw-border-[var(--color-border-white)]">
                <Text className="tw-flex tw-justify-self-center tw-text-[14px] tw-font-normal">
                  {t('continueWith')}
                </Text>
              </Divider>

              <Row className="tw-w-full">
                <Col span={8} className="tw-px-1">
                  <SocialButton
                    provider="microsoft"
                    label={t('microsoftButtonLabel')}
                    onClick={loginMicrosoftUser}
                    loading={isLoading}
                  />
                </Col>
                <Col span={8} className="tw-px-1">
                  <SocialButton
                    provider="google"
                    label={t('googleButtonLabel')}
                    onClick={loginGoogleUser}
                    loading={isLoading}
                  />
                </Col>
                <Col span={8} className="tw-px-1">
                  <SocialButton
                    provider="email"
                    label={t('passwordFreeButtonLabel')}
                    onClick={loginWithMagicLink}
                    loading={isLoading}
                  />
                </Col>
              </Row>

              <Text className="tw-flex tw-justify-center  tw-text-[14px] tw-font-normal">
                {t('signInWithEmail')}
              </Text>

              <Form
                requiredMark
                className="tw-w-full"
                layout="vertical"
                onFinish={loginUser}
                initialValues={{
                  email: location.state?.email || '',
                  trialFeature: location.state?.trialFeature || '',
                }}
              >
                <InputWithFormItem
                  inputName="email"
                  size="middle"
                  placeholderText={t('emailInputPlaceholder')}
                  PrefixIcon={MdOutlineMailOutline}
                  rules={[
                    { required: true, message: t('emailIsRequired') },
                    { type: 'email', message: t('emailIsNotValid') },
                  ]}
                />

                <InputWithFormItem
                  inputName="password"
                  type="password"
                  size="middle"
                  PrefixIcon={MdOutlineKey}
                  onChange={onChangePassword}
                  placeholderText={t('passwordInputPlaceholder')}
                  rules={[
                    { required: true, message: t('passwordIsRequired') },
                    { min: 8, message: t('passwordLength') },
                  ]}
                  errorMessage={
                    isEmailPasswordError && errorMessage ? errorMessage : ''
                  }
                />

                <div className="tw-flex tw-justify-end -tw-mt-4 tw-mb-4">
                  <Link
                    to="/password-recovery"
                    className="tw-text-[13px] !tw-text-[var(--color-secondary)] tw-font-semibold hover:tw-underline"
                  >
                    {t('forgotPassword')}
                  </Link>
                </div>

                <Button
                  submit
                  fullWidth
                  size="middle"
                  type="primary"
                  label={t('signInBtn')}
                  loading={isLoading}
                />
              </Form>

              <Divider className="!tw-my-1 !tw-border-[var(--color-border-white)]" />

              <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                <AuthSwitchLink
                  question={t('dontHaveAccount')}
                  ctaLabel={t('signUpCtaTextButton')}
                  to={SIGN_UP_PATH}
                  onClick={() =>
                    dispatch(uiError({ isError: false, message: null }))
                  }
                />
                <ContactUs page="signin" />
              </div>
            </Space>
          </SurfaceContainer>
        </div>
      </CenterPanel>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  isLoading: state.ui.isLoading,
  errorMessage: state.ui.error.message,
  isEmailPasswordError: state.ui.error.isEmailPasswordError,
});

export default connect(mapStateToProps, {
  loginMicrosoftUser: loginMicrosoftUserAction,
  loginGoogleUser: loginGoogleUserAction,
  loginUser: loginUserAction,
})(SignInForm);
